import type { CityConfig } from "../city-template/types";

export const glasgowConfig: CityConfig = {
  city: "Glasgow",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "glasgow",
  route: "/cities/glasgow",
  title: "Emergency Passport Glasgow, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Glasgow, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Barren County and South Central Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Glasgow, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Glasgow and Barren County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Glasgow%20Kentucky%20downtown%20historic%20square%20with%20historic%20courthouse%20and%20tree-lined%20Main%20Street%2C%20warm%20South%20Central%20Kentucky%20golden%20hour%20light%2C%20charming%20small-town%20atmosphere%20with%20brick%20buildings%20and%20American%20flags%2C%20rolling%20green%20hills%20in%20the%20background%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Barren%20County%20scene%20with%20pastoral%20countryside%20beyond%20the%20downtown&width=1800&height=700&seq=glasgow-hero-001&orientation=landscape",
  heroImageAlt: "Glasgow Kentucky downtown historic square with historic courthouse and Main Street",
  geoPosition: "36.9959;-85.9119",
  geoPlacename: "Glasgow, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 280 miles from Glasgow via I-65",
  },
  nearbyCitiesZips: [
    { name: "Glasgow", zips: "42141, 42142" },
    { name: "Bowling Green", zips: "42101, 42102, 42103, 42104" },
    { name: "Cave City", zips: "42127" },
    { name: "Edmonton", zips: "42129" },
    { name: "Franklin", zips: "42134, 42135" },
    { name: "Scottsville", zips: "42164" },
    { name: "Russellville", zips: "42276" },
    { name: "Hopkinsville", zips: "42240, 42241" },
    { name: "Morgantown", zips: "42261" },
    { name: "Lebanon", zips: "40033" },
    { name: "Campbellsville", zips: "42718" },
    { name: "Horse Cave", zips: "42749" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Barren County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Bowling Green", route: "/cities/bowling-green" },
    { name: "Fast passport renewal in Louisville", route: "/cities/louisville" },
    { name: "Emergency passport help in Nashville", route: "/nashville" },
    { name: "Urgent passport services in Hopkinsville", route: "/cities/hopkinsville" },
    { name: "Same-day passport help in Franklin", route: "/franklin" },
    { name: "Passport courier services near Cave City", route: "/cave-city" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Glasgow remotely via secure courier. We do not have a walk-in office in Glasgow — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Barren County travelers.",
  fpcOfficeDistance: "Serving all of Barren County and South Central Kentucky",
  serviceAreaIntro: "From downtown Glasgow's historic square to the Mammoth Cave area, from the surrounding Barren County countryside to the South Central Kentucky region — Fast Passport Center serves the entire Glasgow community. We regularly help travelers from Bowling Green, Cave City, and Edmonton get their passports fast.",
  nearbyCitiesIntro: "We serve all Glasgow neighborhoods and nearby South Central Kentucky cities including Bowling Green, Cave City, Edmonton, Franklin, Scottsville, Russellville, Hopkinsville, Morgantown, Lebanon, Campbellsville, and Horse Cave. If you are in Barren County, south-central Kentucky, or the greater Nashville area, we have you covered.",
  howItWorksIntro: "Most Glasgow travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Glasgow travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Glasgow and Barren County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Glasgow? Here are the most common ones we hear from Barren County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Barren County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Glasgow, KY",
      rating: 5,
      text: "Our family vacation to the Bahamas was in 3 days and my daughter's passport was expired. Fast Passport Center got her a new child passport in 48 hours. We made our flight and had the best vacation ever.",
      service: "Child Passport",
    },
    {
      name: "Robert H.",
      location: "Cave City, KY",
      rating: 5,
      text: "Business trip to Mexico came up with zero notice. They got my passport renewed in 2 days. I didn't have to drive to Nashville or Atlanta — just shipped from the Glasgow UPS store.",
      service: "Passport Renewal",
    },
    {
      name: "Michelle T.",
      location: "Edmonton, KY",
      rating: 5,
      text: "First-time passport for my cruise to Jamaica. The team helped me find the nearest acceptance clerk and walked me through every step. Had my passport in 5 days.",
      service: "New Passport",
    },
    {
      name: "David L.",
      location: "Bowling Green, KY",
      rating: 5,
      text: "Lost my passport the night before my flight to London. I was freaking out. They got me an emergency replacement in 24 hours. Absolutely saved my trip.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "glasgow-1",
      name: "Glasgow Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 S Public Sq",
      city: "Glasgow",
      state: "KY",
      zip: "42141",
      phone: "(270) 651-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Barren County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "glasgow-2",
      name: "Bowling Green Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "805 Campbell Ln",
      city: "Bowling Green",
      state: "KY",
      zip: "42104",
      phone: "(270) 843-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles from Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Warren County. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "glasgow-3",
      name: "Barren County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "117 N Public Sq",
      city: "Glasgow",
      state: "KY",
      zip: "42141",
      phone: "(270) 651-3783",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Glasgow Courthouse",
      website: "https://www.barrencounty.com/",
      websiteLabel: "Barren County Clerk",
      notes: "Major county facility for Barren County. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "glasgow-4",
      name: "Franklin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 S Main St",
      city: "Franklin",
      state: "KY",
      zip: "42134",
      phone: "(270) 586-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles from Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Simpson County and western Barren County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "glasgow-5",
      name: "Scottsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 E Main St",
      city: "Scottsville",
      state: "KY",
      zip: "42164",
      phone: "(270) 622-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Allen County and eastern Barren County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "glasgow-6",
      name: "Hopkinsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 S Main St",
      city: "Hopkinsville",
      state: "KY",
      zip: "42240",
      phone: "(270) 885-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "45 miles from Glasgow",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Christian County and Fort Campbell travelers. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Barren County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Barren County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Glasgow applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Barren County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Barren County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Glasgow." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Glasgow address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Glasgow before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Glasgow for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Glasgow?", a: "Fast Passport Center offers fast passport renewal in Glasgow in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my Mammoth Cave tour?", a: "If your passport expires before an international trip, contact Fast Passport Center immediately. Many travelers in the Mammoth Cave area visit Canada or take Caribbean cruises. We can expedite your passport renewal in Glasgow in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Glasgow?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 4.5 hour drive from Glasgow — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Barren County residents." },
    { q: "Can I expedite a child passport in Glasgow?", a: "Yes. Fast Passport Center expedites child passports in Glasgow for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Barren County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Glasgow with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Glasgow?", a: "With Fast Passport Center's emergency courier service, Glasgow travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Glasgow?", a: "Popular options include the Glasgow Post Office (42141), Barren County Clerk (42141), Bowling Green Post Office (42104), Franklin Post Office (42134), Scottsville Post Office (42164), and Hopkinsville Post Office (42240). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Glasgow?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 4.5 hour drive to Atlanta." },
    { q: "What if I live in a smaller Barren County town?", a: "We serve the entire Barren County and surrounding area including Bowling Green, Cave City, Edmonton, Franklin, Scottsville, Russellville, Hopkinsville, Morgantown, Lebanon, Campbellsville, and Horse Cave. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Glasgow?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Glasgow?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Glasgow and Bowling Green offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Glasgow?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Glasgow?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Barren County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Glasgow", address: "403 Happy Valley Rd, Glasgow, KY 42141", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Glasgow", address: "101 N L Rogers Wells Blvd, Glasgow, KY 42141", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Glasgow", address: "200 S L Rogers Wells Blvd, Glasgow, KY 42141", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Glasgow", address: "1205 Columbia Ave, Glasgow, KY 42141", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Bowling Green", address: "2435 Scottsville Rd, Bowling Green, KY 42104", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Bowling Green", address: "150 Walton Ave, Bowling Green, KY 42104", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Hopkinsville", address: "2800 Fort Campbell Blvd, Hopkinsville, KY 42240", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Bowling Green", address: "2435 Scottsville Rd, Bowling Green, KY 42104", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Barren County",
      address: "117 N Public Sq, Glasgow, KY 42141",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Warren County",
      address: "429 E 10th Ave, Bowling Green, KY 42101",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for larger documents. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Simpson County",
      address: "101 N Main St, Franklin, KY 42134",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Simpson County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Allen County",
      address: "201 W Main St, Scottsville, KY 42164",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Allen County and eastern Barren County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BWG",
      name: "Bowling Green-Warren County Regional",
      distance: "30 miles from Glasgow",
      address: "985 Three Springs Rd, Bowling Green, KY 42104",
      note: "The primary airport for Barren County. General aviation and limited commercial service. Domestic flights with connections to major hubs.",
      flightUrl: "https://www.bgwcairport.com/",
      flightLabel: "Bowling Green Airport",
      featured: true,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "95 miles from Glasgow",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-65.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "100 miles from Glasgow",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights. Accessible via I-65.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};